import { Suspense } from 'react'
import { PageSeo } from '@/components/SEOHead'
import { buildHomeSeo, resolveSeo } from '@/lib/seo'
import { HeroSearch } from '@/components/home/HeroSearch'
import { HomePagePropertySections } from '@/components/home/HomePagePropertySections'
import { PopularCities } from '@/components/home/PopularCities'
import { FreshProjects } from '@/components/home/FreshProjects'
import { lazyWithRetry } from '@/lib/lazyWithRetry'
import { PropertyCategories } from '@/components/home/PropertyCategories'
import { VideoShowcase } from '@/components/home/VideoShowcase'
import { LatestBlogs } from '@/components/home/LatestBlogs'
import { MagazineSection } from '@/components/home/MagazineSection'
import { MediaInsightsSection } from '@/components/media/MediaInsightsSection'
import { BrokerageCalculator } from '@/components/home/BrokerageCalculator'
import { ToolsResourcesSection } from '@/components/home/ToolsResourcesSection'
import { Testimonials } from '@/components/home/Testimonials'
import { HomeFAQ } from '@/components/home/HomeFAQ'
import { homeFaqs } from '@/data/mock/faq'
import { HomeCTAFeedbackRow } from '@/components/home/HomeCTAFeedbackRow'
import { useHomeData } from '@/hooks/api/useHomeData'
import { useFaqs } from '@/hooks/api/useFaqs'
import { useTestimonials } from '@/hooks/api/useTestimonials'
import type { ShowcaseVideo } from '@/types/home'
import type { Testimonial } from '@/types/home'
import type { CustomerTestimonial } from '@/types/testimonial'

/** Set to `true` to show the homepage "Properties on Map" section again. */
const SHOW_HOME_PROPERTY_MAP = false

// Leaflet (JS + CSS) stays out of the home bundle unless the map section is enabled.
const PropertyMapSection = lazyWithRetry(() =>
  import('@/components/home/PropertyMapSection').then((m) => ({ default: m.PropertyMapSection })),
)

function toHomeTestimonial(t: CustomerTestimonial): Testimonial | null {
  const content = t.description?.trim()
  if (!content) return null

  const rating = Math.max(1, Math.min(5, Math.round(t.rating ?? 5)))

  return {
    id: t.id,
    name: t.name?.trim() || 'Customer',
    role: t.designation?.trim() || 'Customer',
    content,
    avatar: t.image || '',
    rating,
  }
}

function toShowcaseVideo(t: CustomerTestimonial): ShowcaseVideo {
  const designation = t.designation?.trim()
  const location = designation && designation !== '.' ? designation : undefined

  return {
    id: t.id,
    title: t.name || 'Customer Story',
    platform: 'youtube',
    videoId: t.videoId ?? undefined,
    thumbnail: t.thumbnail ?? undefined,
    location,
  }
}

export function HomePage() {
  const { data, isLoading } = useHomeData()
  const { items: faqs } = useFaqs()
  const homeFaqItems = faqs.slice(0, 6)

  const { data: videoTestimonials } = useTestimonials('video')
  const { data: textTestimonials } = useTestimonials('text')
  const showcaseVideos: ShowcaseVideo[] = (videoTestimonials ?? [])
    .filter((t) => t.videoId)
    .map(toShowcaseVideo)
  const customerStories = (textTestimonials ?? [])
    .map(toHomeTestimonial)
    .filter((t): t is Testimonial => t !== null)

  return (
    <>
      <PageSeo seo={resolveSeo(buildHomeSeo())} />
      <HeroSearch />
      <HomePagePropertySections
        premiumProjects={data.premiumProjects}
        recommendedForSale={data.recommendedForSale}
        recommendedForRent={data.recommendedForRent}
        isLoading={isLoading}
      />
      <FreshProjects slides={data.freshProjectSlides} />
      {SHOW_HOME_PROPERTY_MAP ? (
        <Suspense fallback={null}>
          <PropertyMapSection />
        </Suspense>
      ) : null}
      <PopularCities cities={data.popularCities} />
      <PropertyCategories categories={data.categories} />
      <div id="brokerage-calculator">
        <BrokerageCalculator />
      </div>
      <ToolsResourcesSection />
      <VideoShowcase videos={showcaseVideos.length ? showcaseVideos : data.showcaseVideos} />
      {customerStories.length ? <Testimonials testimonials={customerStories} /> : null}
      <MagazineSection />
      <MediaInsightsSection />
      <LatestBlogs blogs={data.blogs} />
      <HomeFAQ faqs={homeFaqItems.length ? homeFaqItems : homeFaqs} />
      <HomeCTAFeedbackRow />
    </>
  )
}
